import { getSystemById } from '../../data/ttrpgSystems';
import { Button, BUTTON_ICONS } from '../../../../shared/components/Button';
import '../../../../shared/styles/Orchestrator.css';

/**
 * SystemDmScreenModal
 *
 * Full-screen overlay (below the 80px TopBar) that shows a TTRPG system
 * reference / DM screen.  It does NOT cover the TopBar.
 *
 * Props:
 *   visible          – boolean
 *   systemId         – id string from TTRPG_SYSTEMS
 *   onClose          – () => void
 *   onChangeSystem   – () => void  (opens SystemSelectModal)
 *   mode             – 'light' | 'dark'
 */
function SystemDmScreenModal({
  visible,
  systemId,
  onClose,
  onChangeSystem,
  mode = 'dark',
}) {
  if (!visible) return null;

  const isDark = mode !== 'light';
  const system = getSystemById(systemId);

  if (!system) return null;

  const overlayBg = isDark ? 'rgba(20,22,26,0.97)' : 'rgba(246,247,235,0.97)';
  const cardBg = isDark ? 'var(--onyx)' : '#fff';
  const borderColor = isDark ? 'var(--jet)' : '#e4e7c5';
  const textColor = isDark ? 'var(--ivory)' : 'var(--jet)';
  const labelColor = isDark ? '#aaa' : '#555';
  const valueColor = isDark ? 'var(--ivory)' : 'var(--jet)';
  const iconIsComponent = system.buttonIcon && typeof system.buttonIcon !== 'string';
  const IconComponent = iconIsComponent ? system.buttonIcon : null;
  const iconSrc = !iconIsComponent ? BUTTON_ICONS[system.buttonIcon] : null;
  const lightIconFilter =
    'brightness(0) saturate(100%) invert(10%) sepia(6%) saturate(470%) hue-rotate(159deg) brightness(94%) contrast(88%)';

  return (
    <div
      className='system_dm_screen_overlay'
      style={{
        position: 'fixed',
        top: 80,
        left: 0,
        right: 0,
        bottom: 0,

        background: overlayBg,
        overflowY: 'auto',
        padding: '20px 24px',
        color: textColor,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            border: `1px solid ${borderColor}`,
            background: cardBg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden
        >
          {IconComponent && (
            <IconComponent
              size={22}
              strokeWidth={2.4}
              strokeLinecap='round'
              strokeLinejoin='round'
              color={textColor}
            />
          )}
          {!IconComponent && iconSrc && (
            <img
              src={iconSrc}
              alt=''
              style={{
                width: 22,
                height: 22,
                objectFit: 'contain',
                filter: isDark ? 'none' : lightIconFilter,
              }}
            />
          )}
        </div>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          {system.name} — GM Reference
        </h2>
      </div>

      {/* DM Screen content cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 16,
          paddingBottom: 80,
        }}
      >
        {(system.dmScreen || []).map((section) => (
          <div
            key={section.title}
            style={{
              background: cardBg,
              border: `1px solid ${borderColor}`,
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            {/* Card header */}
            <div
              style={{
                background: isDark ? 'var(--jet)' : '#e94f37',
                color: isDark ? 'var(--ivory)' : '#fff',
                padding: '8px 14px',
                fontWeight: 700,
                fontSize: '0.9rem',
                letterSpacing: '0.03em',
              }}
            >
              {section.title}
            </div>

            {/* Card rows */}
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.82rem',
              }}
            >
              <tbody>
                {(section.rows || []).map((row, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: `1px solid ${borderColor}`,
                      background:
                        idx % 2 === 0
                          ? 'transparent'
                          : isDark
                          ? 'rgba(255,255,255,0.04)'
                          : 'rgba(0,0,0,0.03)',
                    }}
                  >
                    <td
                      style={{
                        padding: '6px 14px',
                        fontWeight: 600,
                        color: labelColor,
                        whiteSpace: 'nowrap',
                        verticalAlign: 'top',
                        width: '35%',
                      }}
                    >
                      {row.label}
                    </td>
                    <td
                      style={{
                        padding: '6px 14px',
                        color: valueColor,
                        verticalAlign: 'top',
                      }}
                    >
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Sticky bottom action bar */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px',
          background: overlayBg,
          borderTop: `1px solid ${borderColor}`,
        }}
      >
        <Button
          label='Change System'
          type={isDark ? 'onyx' : 'cinnabar'}
          onPress={() => {
            onClose();
            onChangeSystem();
          }}
        />
        <Button
          label='Close'
          type={isDark ? 'onyx' : 'black-white'}
          onPress={onClose}
        />
      </div>
    </div>
  );
}

export default SystemDmScreenModal;
